import { put } from 'redux-saga/effects'
import { displayErrorModal } from 'actions/application'
import { NetworkError } from 'utils/request'
import strings from 'utils/strings'
import { HANDLED, SKIP } from './constants'

/**
 * Domain handler — Reference Parameter save failure.
 *
 * Shows a domain-specific error modal when an RP save fails, including the
 * error code and description from the backend response.
 *
 * This handler only fires for APPLICATION-LEVEL errors — errors that carry a
 * `.code` or `.description` set by the RP endpoint. Network-level errors
 * (fetch failures, timeouts, server 5xx) return SKIP so the system handlers
 * and defaultErrorHandler can give the correct, non-misleading UI. Without
 * this guard, a CORS error, timeout, or server 500 would show "reference
 * parameter save failed" instead of the correct timeout/network error modal.
 *
 * ⚠ ORDERING: This handler returns HANDLED unconditionally for application
 * errors. If you need to combine it with a domain-specific handler like
 * ptpErrorHandler that has its own guard, always place ptpErrorHandler (or
 * any other guarded handler) FIRST in the errorHandlers array.
 *
 * Usage:
 *   apiAction({
 *     ...,
 *     errorHandlers: [ptpErrorHandler, rpSaveErrorHandler], // ptpErrorHandler must come first
 *   })
 *
 * @param {Error} err - The caught error with optional `.code` and `.description`.
 * @returns {'handled'|'skip'}
 */
export default function* rpSaveErrorHandler(err) {
  // Skip all network-level errors:
  //   - TypeError          — fetch failure (offline, CORS, CSP)
  //   - NetworkError       — HTTP 4xx/5xx responses (base class; also covers
  //                          NetworkTimeoutError which extends NetworkError)
  // These should fall through to system handlers or defaultErrorHandler so
  // the user sees a generic network/timeout UI rather than the misleading
  // "reference parameter save failed" copy.
  if (err instanceof NetworkError || err instanceof TypeError) {
    return SKIP
  }
  // Also skip if there are no application-level metadata fields — this
  // indicates an unexpected error thrown outside the normal API path.
  // We require at least .code or .description (fields set by the RP endpoint)
  // rather than .message, because every Error subclass (SyntaxError, RangeError,
  // etc.) has a .message. Without this tightening, any non-TypeError/non-NetworkError
  // would show "Reference Parameter save failed" — misleading for parse errors
  // or other unexpected throws unrelated to RP.
  if (!err.code && !err.description) {
    return SKIP
  }

  // At this point err.code or err.description (or both) are truthy — the guard
  // above ensures this. The else branch is unreachable; use the unconditional form.
  const detail = `${err.code || 'Error'}: ${
    err.description || err.message || 'Unknown error'
  }`
  yield put(
    displayErrorModal(strings.reference_parameter_save_failed, [detail])
  )

  return HANDLED
}
